import { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  disableIsDirectToInvoices,
  updateBuyerSupplierInvoicesDataWeeksLimit,
} from '../../../../redux/globalSlices/buyersSlice';
import { getGroupedBuyersSuppliers } from '../../../../redux/services/buyersService';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { SupplierForBuyer, roundTo, AgingRange, DEFAULT_AGING_RANGES } from '@dill/dill-shared';
import { DillButton } from '../../../general/Dill';
import distributorPlaceholder from '../../../../assets/svg/distributor_placeholder.svg';
import emptyListImg from '../../../../assets/images/emptyList.png';
import { updateSelectedSupplierForBuyer } from '../../../../redux/globalSlices/invoiceSlice';
import { getMainBuyerPaymentMethods } from '../../../../redux/services/paymentsService';
import AppAddPaymentMethodModal from '../../../general/AppAddPaymentMethodModal/AppAddPaymentMethodModal';
import { useParamMainBuyerId } from '../../../../utils/hooks/useParamMainBuyerId';

export const Suppliers = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const {
    selectedBuyerSuppliers,
    isDirectToInvoices,
    loadingList: buyersLoadingList,
    buyerSupplierInvoicesDataWeeksLimit,
  } = useAppSelector((state) => state.buyers);
  const { user } = useAppSelector((state) => state.auth);
  const { selectedMainBuyer } = useAppSelector((state) => state.buyers);
  const { isPreviewMode } = useAppSelector((state) => state.preview);

  const [suppliers, setSuppliers] = useState<SupplierForBuyer[]>([]);
  const [openAutopayModal, setOpenAutopayModal] = useState<boolean>(false);
  const [selectedMainBuyerId] = useParamMainBuyerId();
  const [supplierName, setSupplierName] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [buyerPaymentMethodId, setBuyerPaymentMethodId] = useState('');
  const [updating, setUpdating] = useState<boolean>(false);
  const [openPaymentMethod, setOpenPaymentMethod] = useState<boolean>(false);
  const mainRoles = user?.roles.filter((role) => role !== 'DILL_ADMIN');

  useEffect(() => {
    dispatch(updateSelectedSupplierForBuyer(null));

    return () => {};
  }, []);
  useEffect(() => {
    setSuppliers(selectedBuyerSuppliers);

    return () => {};
  }, [selectedBuyerSuppliers]);

  useEffect(() => {
    if (selectedBuyerSuppliers.length > 0) {
      dispatch(updateSelectedSupplierForBuyer(selectedBuyerSuppliers[0]));
    }
  }, [selectedBuyerSuppliers]);

  useEffect(() => {
    if (selectedMainBuyerId && user?.userMainBuyers && !isPreviewMode) {
      const mainBuyer = user?.userMainBuyers
        .filter((mb) => !mb?.isDeactivated && !mb?.isCODCustomer)
        .find((mb) => mb.id === selectedMainBuyerId);
      if (!mainBuyer) {
        navigate('/companies');
      }
    }
  }, [user?.userMainBuyers, isPreviewMode]);

  useEffect(() => {
    if (selectedMainBuyerId) {
      dispatch(updateBuyerSupplierInvoicesDataWeeksLimit(2));
      dispatch(
        getGroupedBuyersSuppliers({
          mainBuyerId: selectedMainBuyerId,
          weeksLimit: 2,
        })
      );
    }

    return () => {};
  }, [selectedMainBuyerId]);

  useEffect(() => {
    if (selectedMainBuyerId && user?.userMainBuyers) {
      const mainBuyer = user?.userMainBuyers
        .filter((mb) => !mb?.isDeactivated && !mb?.isCODCustomer)
        .find((mb) => mb.id === selectedMainBuyerId);
      if (!mainBuyer) {
        navigate('/companies');
      }
    }
  }, [user?.userMainBuyers]);

  useEffect(() => {
    if (
      suppliers &&
      suppliers.length === 1 &&
      params.restaurantGroupId &&
      location.pathname.startsWith(`/companies/${selectedMainBuyerId}/invoices/`) &&
      isDirectToInvoices
    ) {
      navigate(`/companies/${selectedMainBuyerId}/invoices/${suppliers[0].domainId}`);
      dispatch(disableIsDirectToInvoices());
    }

    return () => {};
  }, [suppliers]);

  const handleSaveAutopay = async (autopay: boolean, paymentMethodId: string) => {
    // setUpdating(true);
    // const buyerSelected = selectedBuyersGroup?.buyers.find(
    //   (buyer) => buyer.supplierId === supplierId
    // );
    // await dispatch(
    //   updateBuyer({
    //     buyerId: buyerSelected?.id ?? '',
    //     buyerDetails: {
    //       autopayDetails: {
    //         paymentMethodId: paymentMethodId,
    //         userId: user?.id ?? '',
    //       },
    //       isAutopayActive: autopay,
    //     },
    //   })
    // );
    // setUpdating(false);
    // setOpenAutopayModal(false);
  };

  const isAutoPaySelected = useMemo(() => {
    // const buyerSelected = selectedBuyersGroup?.buyers.find(
    //   (buyer) => buyer.supplierId === supplierId
    // );
    // if (buyerSelected && buyerSelected?.isAutopayActive) {
    //   setBuyerPaymentMethodId(buyerSelected.autopayDetails?.paymentMethodId ?? '');
    //   return buyerSelected.isAutopayActive;
    // }
    return false;
  }, [supplierId, selectedMainBuyer]);

  const fmt = (val: string | number | null | undefined) =>
    `$${roundTo(parseFloat(val?.toString() || '0'), 2)}`;

  return (
    <div className="h-full w-full flex overflow-hidden p-4">
      <AppAddPaymentMethodModal
        open={openPaymentMethod}
        handleClose={() => setOpenPaymentMethod(false)}
        paymentOptionsToShow="BANK"
        userType="buyer"
      />

      {/* ── Supplier grid ── */}
      {suppliers.length > 0 && (
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {suppliers.map((supplier, supplierIndex) => {
              const summary = supplier?.buyer?.documentsSummary;
              const isUseAgingRanges = supplier?.isUseAgingRanges ?? false;
              const totalDue = fmt(summary?.totalAmountDueInvoices);

              return (
                <div
                  key={supplierIndex + 'oo'}
                  className="bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition-shadow flex flex-col overflow-hidden w-[270px]">
                  <div className="flex w-full justify-center p-4 flex-col">
                    <div
                      className={
                        'GREY_100-BG  rounded-lg flex items-center ' +
                        (supplier?.logo?.url ? 'w-full' : '')
                      }>
                      {!supplier.logo && <img className="m-4" src={distributorPlaceholder} />}
                      {supplier?.logo?.url && (
                        <img className="h-9 m-2 w-full object-contain" src={supplier?.logo?.url} />
                      )}
                    </div>

                    <p className="font-bold text-base mt-2 text-center w-full">{supplier.name}</p>
                  </div>

                  {/* Financial rows */}
                  <div className="flex-1 px-4 py-4 space-y-2">
                    {/* Current Due */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs">Current Due</span>
                      <span className="text-xs text-gray-500">
                        {fmt(summary?.totalCurrentDueInvoices)}
                      </span>
                    </div>

                    {/* Aging rows */}
                    {isUseAgingRanges ? (
                      [
                        ...(supplier?.agingRanges && supplier.agingRanges.length > 0
                          ? supplier.agingRanges
                          : DEFAULT_AGING_RANGES),
                      ]
                        .sort((a, b) => a.order - b.order)
                        .map((range) => {
                          const agingBuckets = summary?.agingBuckets;
                          const bucket = Array.isArray(agingBuckets)
                            ? agingBuckets.find((b) => b.order === range.order)
                            : undefined;
                          return (
                            <div
                              key={`aging_${range.order}`}
                              className="flex items-center justify-between">
                              <span className="text-xs">{range.label} Days Past Due</span>
                              <span className="text-xs text-gray-500">
                                {fmt(bucket?.amount ?? 0)}
                              </span>
                            </div>
                          );
                        })
                    ) : (
                      <>
                        <div className="flex items-center justify-between">
                          <span className="text-xs">1–30 Days Past Due</span>
                          <span className="text-xs text-gray-500">
                            {fmt(summary?.totalPastDue1To30Invoices)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs">31–60 Days Past Due</span>
                          <span className="text-xs text-gray-500">
                            {fmt(summary?.totalPastDue31To60Invoices)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs">61–90 Days Past Due</span>
                          <span className="text-xs text-gray-500">
                            {fmt(summary?.totalPastDue61To90Invoices)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs">Over 90 Days Past Due</span>
                          <span className="text-xs text-gray-500">
                            {fmt(summary?.totalPastDueOver90Invoices)}
                          </span>
                        </div>
                      </>
                    )}

                    {/* Total */}
                    <div className="mt-1 pt-3 border-t border-dashed border-gray-200 flex items-center justify-between">
                      <span className="text-xs font-semibold text-gray-700">Total Amount Due</span>
                      <span className="text-xs font-bold text-gray-900">{totalDue}</span>
                    </div>
                  </div>

                  {/* Action */}
                  <div className="px-4 pb-4">
                    <DillButton
                      label="View Invoices"
                      variant="primary"
                      onClick={() => {
                        dispatch(updateSelectedSupplierForBuyer(supplier));
                        navigate(`/companies/${selectedMainBuyerId}/invoices/${supplier.domainId}`);
                      }}
                      className="w-full justify-center"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Empty state ── */}
      {suppliers.length < 1 && (
        <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
          <img className="w-48 object-contain opacity-60 mb-6" src={emptyListImg} alt="" />
          <p className="text-sm font-medium text-gray-700 max-w-sm leading-relaxed">
            Your suppliers have not yet signed up for Dill invoicing &amp; payments. If you would
            like to get automated lien waivers with payments, get your suppliers on Dill by reaching
            out to customer support.
          </p>
        </div>
      )}
    </div>
  );
};
